import { isAndroidApp } from "./platform";
import { isMobile } from "../constants/dimension";

const HELPSHIFT_SCRIPT_ID = "hs-chat";
const HELPSHIFT_SCRIPT_URL = "https://webchat.helpshift.com/latest/webChat.js";
const HELPSHIFT_WEB_KEYBOARD_STYLE_ID = "hs-web-keyboard-inset";

const HELPSHIFT_KEYBOARD_INSET_VAR = "--hs-keyboard-inset";

const MOBILE_WEB_KEYBOARD_CSS = `
  #hs-web-sdk-iframe {
    bottom: var(${HELPSHIFT_KEYBOARD_INSET_VAR}, 0px) !important;
    height: calc(100% - var(${HELPSHIFT_KEYBOARD_INSET_VAR}, 0px)) !important;
  }
`;

function injectStyle(id: string, css: string): void {
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Dynamically loads the Helpshift webchat SDK.
 * Must be called AFTER splash screen finishes to avoid
 * conflicts with hardware media decoder on smart TVs.
 */
export function loadHelpshift(): void {
  if (document.getElementById(HELPSHIFT_SCRIPT_ID)) return;
  if (isMobile) {
    injectStyle(HELPSHIFT_WEB_KEYBOARD_STYLE_ID, MOBILE_WEB_KEYBOARD_CSS);
  }

  window.helpshiftConfig = {
    platformId: "play-onmo_platform_20251003101406562-a4d6cfdecff7103",
    domain: "play-onmo",
    language: "en",
    widgetOptions: {
      showLauncher: false,
    },
  } as unknown as typeof window.helpshiftConfig;

  if (typeof window.Helpshift !== "function") {
    const queue = function (...args: unknown[]) {
      queue.q.push(args);
    } as typeof window.Helpshift & { q: unknown[][] };
    queue.q = [];
    window.Helpshift = queue as typeof window.Helpshift;
  }

  const script = document.createElement("script");
  script.async = true;
  script.id = HELPSHIFT_SCRIPT_ID;
  script.src = HELPSHIFT_SCRIPT_URL;
  script.addEventListener("load", () => window.Helpshift("init"));

  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

export function trackHelpshiftKeyboardInset(): () => void {
  const viewport = window.visualViewport;
  if (!isMobile || isAndroidApp || !viewport) return () => {};

  const root = document.documentElement;
  const update = () => {
    const inset = document.fullscreenElement
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;
    root.style.setProperty(HELPSHIFT_KEYBOARD_INSET_VAR, `${inset}px`);
  };

  update();
  viewport.addEventListener("resize", update);
  viewport.addEventListener("scroll", update);
  document.addEventListener("fullscreenchange", update);

  return () => {
    viewport.removeEventListener("resize", update);
    viewport.removeEventListener("scroll", update);
    document.removeEventListener("fullscreenchange", update);
    root.style.removeProperty(HELPSHIFT_KEYBOARD_INSET_VAR);
  };
}
